/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file sets up the logic for retrieving an individual patient.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Used to create logic based side effects for the redux store.
 */
import { createLogic } from 'redux-logic';

/**
 * Typings
 */
import { TypedUseSelectorHook, useSelector, useDispatch } from 'react-redux';

/**
 * Utility library for reducing redux boilerplate while remaining typed. 
 */
import { ImmerReducer, createActionCreators, createReducerFunction } from 'immer-reducer';

/**
 * Typings:
 *  - IAuthSession
 */
import { IAuthSession } from '@servicestack/client';

/**
 * Enumeration used to determine the state of a request.
 */
import { RequestState, IRequestState } from '@ngt/request-utilities';


import { useState, useEffect } from 'react';

import { Reducer, bindActionCreators } from 'redux';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to register the logics so that they are included in the store. 
 */
import reducerRegistry from '../reducerRegistry';

/*
 * Used to get access to the API types and requests
 */
import * as Dtos from '../../api/Neon.Dtos';

/**
 * Typings
 */
import { ILogicSideEffect } from '../../utilities/logicTypes';

import client from '../../api/client';

/*
* ---------------------------------------------------------------------------------
* Interfaces / Types
* ---------------------------------------------------------------------------------
*/

interface IInitialAuthenticatedUserState {
    user: IAuthSession | null;
    loadState: IRequestState<Dtos.ResponseStatus>;
}

export interface IAuthenticatedUserState {
    authenticatedUser: IInitialAuthenticatedUserState;
}

/*
 * ---------------------------------------------------------------------------------
 * Initial State
 * ---------------------------------------------------------------------------------
 */

const AuthenticatedUserInitialState: IInitialAuthenticatedUserState = {
    user: null,
    loadState: {
        state: RequestState.None
    }
};

/*
* ---------------------------------------------------------------------------------
* Selectors
* ---------------------------------------------------------------------------------
*/

export const useAuthenticatedUserSelector: TypedUseSelectorHook<IAuthenticatedUserState> = useSelector;

export const AuthenticatedUserSelectors = {
    selectUser: (state: IAuthenticatedUserState) => state.authenticatedUser.user,
    selectLoadState: (state: IAuthenticatedUserState) => state.authenticatedUser.loadState
};

/*
* ---------------------------------------------------------------------------------
* Reducer
* ---------------------------------------------------------------------------------
*/

class AuthenticatedUserReducer extends ImmerReducer<IInitialAuthenticatedUserState> {
    public checkAuthenticatedUser() {
        this.draftState.loadState = {
            state: RequestState.Pending
        };
    }

    public signoutAuthenticatedUser() {
        this.draftState.user = null
    }

    public loadSuccess(user: IAuthSession) {
        this.draftState.user = user;
        this.draftState.loadState = {
            state: RequestState.Success
        };
    }

    public loadFailure(responseStatus: Dtos.ResponseStatus) {
        this.draftState.user = null;
        this.draftState.loadState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public clear() {
        return { ...AuthenticatedUserInitialState };
    }
}

export const AuthenticatedUserActions = createActionCreators(AuthenticatedUserReducer);
const reducer = createReducerFunction(AuthenticatedUserReducer, AuthenticatedUserInitialState);

/*
 * ---------------------------------------------------------------------------------
 * API
 * ---------------------------------------------------------------------------------
 */

export const AuthenticatedUserApi = {
    checkAuthenticatedUser: () => {
        return client.post(new Dtos.Authenticate());
    }
}

/*
 * ---------------------------------------------------------------------------------
 * Logic
 * ---------------------------------------------------------------------------------
 */

const AuthenticatedUserLogic = {
    checkAuthenticatedUser: createLogic({
        type: AuthenticatedUserActions.checkAuthenticatedUser.type,
        latest: true,
        process: async ({ action }: ILogicSideEffect<IAuthenticatedUserState, typeof AuthenticatedUserActions.checkAuthenticatedUser>, dispatch, done) => {
            try {
                const response = await AuthenticatedUserApi.checkAuthenticatedUser();
                dispatch(AuthenticatedUserActions.loadSuccess(
                    response
                ));
            }
            catch (error: any) {
                dispatch(AuthenticatedUserActions.loadFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    })
};

const logics = [
    AuthenticatedUserLogic.checkAuthenticatedUser
]

/*
* ---------------------------------------------------------------------------------
* Hooks
* ---------------------------------------------------------------------------------
*/

export const useAuthenticatedUser = (): [IAuthSession | null, IRequestState<Dtos.ResponseStatus>, typeof AuthenticatedUserActions ] => {
    const user = useSelector(AuthenticatedUserSelectors.selectUser);
    const loadState = useSelector(AuthenticatedUserSelectors.selectLoadState);

    const dispatch = useDispatch();

    const [actions, setActions] = useState<typeof AuthenticatedUserActions>(() => {
        return bindActionCreators(AuthenticatedUserActions, dispatch);
    });

    useEffect(() => {
        setActions(bindActionCreators(AuthenticatedUserActions, dispatch));
    }, [dispatch, setActions]);

    return [
        user,
        loadState,
        actions
    ]
}

/*
 * ---------------------------------------------------------------------------------
 * Register
 * ---------------------------------------------------------------------------------
 */

reducerRegistry.register('authenticatedUser', reducer as any, logics as any);